import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { Upload, CheckCircle2, ShieldCheck } from "lucide-react";
import { http, money, notify, documentTypes } from "../api";

/**
 * Public page opened by the customer via WhatsApp. Reads the token from the URL,
 * confirms the identity of the customer, and lets them upload documents that land
 * directly on the lead's document list. No auth required.
 */
export default function CustomerUpload({ token }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("PAN");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    http.get(`/public/customer-links/${token}`)
      .then((r) => {
        setInfo(r.data);
        setUploaded(r.data.existing_documents || []);
      })
      .catch((err) => setError(err?.response?.data?.detail || "This link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("document_type", type);
      body.append("file", file);
      await http.post(`/public/customer-links/${token}/documents`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notify.success(`${type} uploaded — thank you`);
      setUploaded([...uploaded, type]);
      setFile(null);
      const el = document.getElementById("customer-file-input");
      if (el) el.value = "";
    } catch (err) {
      notify.error(err, "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Toaster position="top-center" richColors closeButton />
      <main className="customer-page">
        <header className="customer-header">
          <div className="brand-mark">RX</div>
          <div>
            <p className="eyebrow">RUPEE XPERT / SECURE UPLOAD</p>
            <h1>Send your documents</h1>
          </div>
        </header>

        {loading ? (
          <div className="customer-card"><p>Verifying your private link…</p></div>
        ) : error ? (
          <div className="customer-card">
            <h2>Link unavailable</h2>
            <p className="muted">{error}</p>
            <p className="muted">Please contact your Rupee Xpert representative for a fresh link.</p>
          </div>
        ) : (
          <>
            <section className="customer-card">
              <p className="eyebrow">CUSTOMER PROFILE</p>
              <h2>{info.customer_name}</h2>
              <p className="muted">
                Ends in {info.mobile_last4} · {info.loan_type} of {money(info.loan_amount)}
                {info.bank_name ? ` · ${info.bank_name}` : ""}
              </p>
              <div className="customer-secure">
                <ShieldCheck size={16} />
                <span>Only you can use this link. It expires on{" "}
                  {info.expires_at ? new Date(info.expires_at).toLocaleDateString("en-IN") : "the agreed date"}.
                </span>
              </div>
            </section>

            <section className="customer-card">
              <h3>Upload a document</h3>
              <form onSubmit={submit} className="customer-form">
                <label>
                  Document type
                  <select
                    data-testid="customer-doc-type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    {documentTypes.filter((t) => t !== "Vehicle documents" && t !== "Property documents").map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Choose file
                  <input
                    id="customer-file-input"
                    data-testid="customer-file-input"
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png,.heic"
                    required
                  />
                </label>
                <p className="muted small">Accepted: PDF, JPG, PNG, HEIC · up to 15 MB.</p>
                <button
                  data-testid="customer-upload-submit"
                  className="primary-button"
                  disabled={!file || busy}
                >
                  <Upload size={15} /> {busy ? "Uploading…" : "Upload securely"}
                </button>
              </form>
            </section>

            {uploaded.length > 0 && (
              <section className="customer-card">
                <h3>Received so far</h3>
                <ul className="customer-received">
                  {uploaded.map((t, i) => (
                    <li key={`${t}-${i}`} data-testid={`customer-received-${i}`}>
                      <CheckCircle2 size={16} /> {t}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <footer className="customer-footer">
          <p className="muted small">
            Rupee Xpert is a loan distribution partner. We share your documents only with the
            bank/NBFC you selected. Never share this link with anyone else.
          </p>
        </footer>
      </main>
    </>
  );
}
